import { code128Modules } from './code128';
import { isValidGtin } from './gs1';

// 1D symbologies a browser draws for labels printed through a normal printer driver: Code 128
// (code128.ts), Code 39, EAN-13 and Interleaved 2 of 5, as bar positions in printer dots.

export interface Bars {
  /** dark bars, left to right, in dots */
  readonly bars: readonly { readonly x: number; readonly w: number }[];
  readonly width: number;
  /** text under (or over) the bars */
  readonly text: string;
}

/** Bars of a module string ("1" dark, "0" light) at `module` dots per module. */
export function barsFromModules(modules: string, module: number, text: string): Bars {
  const bars: { x: number; w: number }[] = [];
  for (let i = 0; i < modules.length; ) {
    if (modules[i] !== '1') {
      i++;
      continue;
    }
    let run = 1;
    while (modules[i + run] === '1') run++;
    bars.push({ x: i * module, w: run * module });
    i += run;
  }
  return { bars, width: modules.length * module, text };
}

/** Bars of a wide/narrow pattern ("n" / "w", starting with a bar). */
function barsFromElements(elements: string, narrow: number, wide: number, text: string): Bars {
  const bars: { x: number; w: number }[] = [];
  let x = 0;
  [...elements].forEach((e, i) => {
    const w = e === 'w' ? wide : narrow;
    if (i % 2 === 0) bars.push({ x, w });
    x += w;
  });
  return { bars, width: x, text };
}

export function code128Bars(text: string, module: number, only?: 'B'): Bars {
  return barsFromModules(code128Modules(text, only), module, text);
}

// --- Code 39 -------------------------------------------------------------------------------
const CODE39 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ-. $/+%';
// 9 elements (bar, space, … bar), 1 = wide; '*' is start / stop
const CODE39_PATTERNS: Readonly<Record<string, string>> = {
  '0': '000110100', '1': '100100001', '2': '001100001', '3': '101100000', '4': '000110001',
  '5': '100110000', '6': '001110000', '7': '000100101', '8': '100100100', '9': '001100100',
  A: '100001001', B: '001001001', C: '101001000', D: '000011001', E: '100011000', F: '001011000',
  G: '000001101', H: '100001100', I: '001001100', J: '000011100', K: '100000011', L: '001000011',
  M: '101000010', N: '000010011', O: '100010010', P: '001010010', Q: '000000111', R: '100000110',
  S: '001000110', T: '000010110', U: '110000001', V: '011000001', W: '111000000', X: '010010001',
  Y: '110010000', Z: '011010000', '-': '010000101', '.': '110000100', ' ': '011000100', '*': '010010100',
  $: '010101000', '/': '010100010', '+': '010001010', '%': '000101010',
};

/** Code 39 with start/stop '*', optional mod-43 check character; ratio = wide / narrow. */
export function code39Bars(data: string, module: number, ratio = 3, checkDigit = false): Bars {
  const text = data.toUpperCase();
  for (const ch of text) if (!CODE39.includes(ch)) throw new RangeError(`not encodable in Code 39: ${JSON.stringify(ch)}`);
  const full = checkDigit ? text + CODE39[[...text].reduce((sum, ch) => sum + CODE39.indexOf(ch), 0) % 43] : text;
  const elements = [...`*${full}*`].map((ch) => [...CODE39_PATTERNS[ch]!].map((b) => (b === '1' ? 'w' : 'n')).join('')).join('n');
  return barsFromElements(elements, module, Math.round(module * ratio), full);
}

// --- EAN-13 --------------------------------------------------------------------------------
const EAN_L = ['0001101', '0011001', '0010011', '0111101', '0100011', '0110001', '0101111', '0111011', '0110111', '0001011'];
const EAN_G = ['0100111', '0110011', '0011011', '0100001', '0011101', '0111001', '0000101', '0010001', '0001001', '0010111'];
const EAN_R = ['1110010', '1100110', '1101100', '1000010', '1011100', '1001110', '1010000', '1000100', '1001000', '1110100'];
const EAN_PARITY = ['LLLLLL', 'LLGLGG', 'LLGGLG', 'LLGGGL', 'LGLLGG', 'LGGLLG', 'LGGGLL', 'LGLGLG', 'LGLGGL', 'LGGLGL'];

export function ean13CheckDigit(first12: string): number {
  const sum = [...first12].reduce((acc, d, i) => acc + Number(d) * (i % 2 === 0 ? 1 : 3), 0);
  return (10 - (sum % 10)) % 10;
}

/** EAN-13 from 12 digits (check digit added) or 13 (check digit verified). */
export function ean13Modules(data: string): { readonly modules: string; readonly text: string } {
  const digits = data.replace(/\D/g, '');
  if (digits.length !== 12 && digits.length !== 13) throw new RangeError(`EAN-13 needs 12 or 13 digits: ${data}`);
  const code = digits.length === 12 ? digits + ean13CheckDigit(digits) : digits;
  if (!isValidGtin(code)) throw new RangeError(`EAN-13 check digit wrong: ${data}`);
  const d = [...code].map(Number);
  const parity = EAN_PARITY[d[0]!]!;
  let modules = '101';
  for (let i = 1; i <= 6; i++) modules += (parity[i - 1] === 'L' ? EAN_L : EAN_G)[d[i]!]!;
  modules += '01010';
  for (let i = 7; i <= 12; i++) modules += EAN_R[d[i]!]!;
  modules += '101';
  return { modules, text: code };
}

export function ean13Bars(data: string, module: number): Bars {
  const { modules, text } = ean13Modules(data);
  return barsFromModules(modules, module, text);
}

// --- Interleaved 2 of 5 ----------------------------------------------------------------------
const I25 = ['nnwwn', 'wnnnw', 'nwnnw', 'wwnnn', 'nnwnw', 'wnwnn', 'nwwnn', 'nnnww', 'wnnwn', 'nwnwn'];

export function interleaved2of5Bars(data: string, module: number, ratio = 3, checkDigit = false): Bars {
  let digits = data.replace(/\D/g, '');
  if (digits.length === 0) throw new RangeError('Interleaved 2 of 5 needs digits');
  if (checkDigit) {
    const sum = [...digits].reverse().reduce((acc, d, i) => acc + Number(d) * (i % 2 === 0 ? 3 : 1), 0);
    digits += String((10 - (sum % 10)) % 10);
  }
  if (digits.length % 2 === 1) digits = `0${digits}`;
  let elements = 'nnnn';
  for (let i = 0; i < digits.length; i += 2) {
    const bars = I25[Number(digits[i])]!;
    const spaces = I25[Number(digits[i + 1])]!;
    for (let k = 0; k < 5; k++) elements += bars[k]! + spaces[k]!;
  }
  elements += 'wnn';
  return barsFromElements(elements, module, Math.round(module * ratio), digits);
}

/** A barcode the scanner should read as a GTIN: EAN-13 when it is one, else Code 128. */
export function productBarcodeKind(barcode: string): 'ean13' | 'code128' {
  return /^\d{13}$/.test(barcode) && isValidGtin(barcode) ? 'ean13' : 'code128';
}
